import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { PusherEvent } from '@mondaygpt/constants'
import { AnimatePresence, motion } from 'framer-motion'
import { Building2, ChevronDown, Loader2, Plug, RotateCcw, Unplug, User } from 'lucide-react'
import { useAnalytics, useIsMobile } from 'hooks'
import { cn } from 'lib/utils'
import { MobilePageHeader } from '../../creative/components/MobilePageHeader'
import { useAppSelector } from 'modules/core'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import { selectBusinessId } from 'modules/authentication'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { useLinkedInConnect } from 'modules/oauth/hooks/useLinkedInConnect'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { usePlatformRolloutEnabled } from '../../feature-flags'
import type { IntegrationResponseDto } from '../../../openapi/models/IntegrationResponseDto'
import { IntegrationsService } from '../../../openapi/services/IntegrationsService'
import { linkedinNeedsTarget, resolveLinkedInTargetSummary } from '../utils/linkedin-display'
import { useGatewayConnect } from '../hooks/use-gateway-connect'
import { GatewayConnectDialogs } from '../components/GatewayConnectDialogs'
import type { FallbackPlatform } from '../api/fallback-api'

export function IntegrationsPage() {
  const { track } = useAnalytics()
  const isMobile = useIsMobile()
  const isRolloutOn = usePlatformRolloutEnabled()
  const businessId = useAppSelector(selectBusinessId)
  const [integrations, setIntegrations] = useState<IntegrationResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null)
  const [disconnectingProvider, setDisconnectingProvider] = useState<string | null>(null)
  const { resolveConnectProvider, chooser } = useLinkedInConnect()

  const fetchIntegrations = useCallback(async () => {
    try {
      const response = await IntegrationsService.integrationControllerGetIntegrationsV1()
      setIntegrations(response.integrations)
    } catch {
      setIntegrations([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Routes the Connect button through the server's gateway policy. For `direct`
  // it returns 'oauth_popup' and we fall through to the existing popup flow; for a
  // partner gateway it drives the redirect/SDK flow and surfaces a Page picker.
  const gatewayConnect = useGatewayConnect({
    onConnected: fetchIntegrations,
  })

  const { resolveAndStart: resolveGateway } = gatewayConnect

  useEffect(() => {
    fetchIntegrations()
  }, [fetchIntegrations])

  // When the server marks a token stale (refresh failed) it pushes this event
  // so the badge appears live without polling. Refetch to pick up the new
  // needsReconnect / isConnected flags. Cheap — IntegrationsPage is rarely open.
  useListenToPusherEvent<{ provider: string; businessIds: string[] }>(
    PusherEvent.OAuthTokenStale,
    () => {
      fetchIntegrations()
    }
  )

  // Restriction set or cleared server-side (provider confirmed a publish
  // restriction, or the recheck probe found it lifted) → refresh so the
  // Restricted badge appears/disappears live without polling.
  useListenToPusherEvent<{ provider: string; businessId: string; restricted: boolean }>(
    PusherEvent.IntegrationRestrictionChanged,
    () => {
      fetchIntegrations()
    }
  )

  // Authoritative refresh after a successful OAuth. The popup-close fallback
  // at runConnect races the server's integration persistence
  // (~800ms gap between FB callback and DB row finalize), so a status check
  // can return isConnected:false and skip the refetch. This event fires
  // post-persist and re-syncs the UI regardless of popup-close timing.
  useListenToPusherEvent<{ provider: string }>(PusherEvent.OAuthUpdated, () => {
    fetchIntegrations()
  })

  // `provider` is the integration row / card the connection lands in — every
  // gateway/status/refetch call keys off it. The popup, however, may open a
  // *different* OAuth app: LinkedIn's single `linkedin` card fronts two apps
  // (`linkedin` personal / `linkedin_org` Company Page). resolveConnectProvider
  // opens the shared chooser (when the gate is on) and returns which app the popup
  // should open — or null if the user dismissed it. Every other provider passes
  // through unchanged, so `oauthProvider === provider`.
  const runConnect = useCallback(
    async (provider: string) => {
      if (!businessId) return
      const oauthProvider = await resolveConnectProvider(provider)
      if (!oauthProvider) return
      setConnectingProvider(provider)
      try {
        // Ask the server which gateway this platform should use. A partner
        // gateway (Composio/Phyllo) is driven by the hook (redirect/SDK +
        // Page picker); `direct` falls through to the existing OAuth popup
        // below. Keyed on the platform (`provider`), not the popup's
        // `oauthProvider` (e.g. linkedin_org).
        const mode = await resolveGateway(provider as FallbackPlatform)
        if (mode === 'failed') {
          toast.error('We couldn’t start the connection', {
            description: 'Please try again in a minute.',
          })
          return
        }
        if (mode !== 'oauth_popup') return

        // source='integrations_page' so social_oauth_initiated and
        // integration_connect_* fire with the same value — required for
        // joining the click-to-success funnel for this surface.
        const result = await oauthUtils.openOAuthPopup(oauthProvider, {
          businessId,
          source: 'integrations_page',
          track,
        })
        if (result === false) {
          track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
            provider,
            platform: provider,
            source: 'integrations_page',
            error: 'oauth_popup_failed',
            error_code: 'oauth_popup_failed',
            error_type: 'popup',
          })
        } else {
          // true or null — verify server-side. Status/refresh key off `provider`
          // (e.g. `linkedin`), not the popup's `oauthProvider` (`linkedin_org`),
          // since the connection lands in the card's row.
          try {
            // Wait for the server to finish persisting the connection before
            // verifying — the popup self-closes ~800ms before the DB write +
            // OAuthUpdated Pusher event, so an immediate status check races the
            // gap and reports isConnected:false on a real success (the "first
            // try fails, second try works" report).
            await oauthUtils.waitForOAuthPersistence(provider)
            const status =
              await IntegrationsService.integrationControllerGetIntegrationStatusV1(provider)
            if (status.isConnected) {
              track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_SUCCESS, {
                provider,
                platform: provider,
                source: 'integrations_page',
              })
              await fetchIntegrations()
            } else {
              track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
                provider,
                platform: provider,
                source: 'integrations_page',
                error: result === null ? 'popup_closed_unconfirmed' : 'verification_not_connected',
                error_code:
                  result === null ? 'popup_closed_unconfirmed' : 'verification_not_connected',
                error_type: 'verification',
              })
            }
          } catch {
            track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
              provider,
              platform: provider,
              source: 'integrations_page',
              error: 'status_request_failed',
              error_code: 'status_request_failed',
              error_type: 'network',
            })
            await fetchIntegrations()
          }
        }
      } finally {
        setConnectingProvider(null)
      }
    },
    [businessId, track, fetchIntegrations, resolveGateway, resolveConnectProvider]
  )

  const handleDisconnect = useCallback(
    async (provider: string) => {
      setDisconnectingProvider(provider)
      try {
        await IntegrationsService.integrationControllerDisconnectIntegrationV1(provider)
        await fetchIntegrations()
      } finally {
        setDisconnectingProvider(null)
      }
    },
    [fetchIntegrations]
  )

  // User declares the provider restriction fixed — clear it and resume. The
  // next publish confirms or re-stamps if the block is actually still in place.
  const handleMarkRestrictionFixed = useCallback(
    async (provider: string) => {
      await IntegrationsService.integrationControllerClearRestrictionV1(provider)
      await fetchIntegrations()
    },
    [fetchIntegrations]
  )

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
      </div>
    )
  }

  // TikTok / X / YouTube / Pinterest stay behind their rollout flags
  // (`enable_tiktok`, …). Instagram / Facebook / LinkedIn have no gate.
  const visibleIntegrations = integrations.filter(
    i =>
      (i.provider === 'instagram' ||
        i.provider === 'facebook' ||
        i.provider === 'linkedin' ||
        i.provider === 'tiktok' ||
        i.provider === 'twitter' ||
        i.provider === 'youtube' ||
        i.provider === 'pinterest') &&
      isRolloutOn(i.provider)
  )

  // Personal and Company Page LinkedIn share a single `linkedin` integration
  // row server-side; the destination lives on `config.linkedinTarget`. The
  // card is labelled "LinkedIn" and its Connect button opens the
  // Personal-vs-Company-Page chooser (which still initiates two OAuth apps —
  // `linkedin` and `linkedin_org` — both landing on this same row).
  const linkedinRow = integrations.find(i => i.provider === 'linkedin')
  const linkedinCard: IntegrationResponseDto | undefined = linkedinRow
    ? { ...linkedinRow, displayName: 'LinkedIn' }
    : undefined
  const linkedinConnecting = connectingProvider === 'linkedin'
  const linkedinDisconnecting = disconnectingProvider === 'linkedin'

  return (
    <div className={isMobile ? 'flex h-full flex-col' : 'mx-auto max-w-2xl px-6 py-6'}>
      {isMobile ? (
        <MobilePageHeader
          title="Integrations"
          subtitle="Connect your accounts to publish from Taka"
        />
      ) : (
        <div className="mb-6">
          <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Integrations
          </span>
          <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.01em] text-paper sm:text-[28px]">
            Connect your accounts
          </h1>
          <p className="mt-1 text-[14px] text-paper-dim">
            Connect your business accounts to publish content directly from Taka.
          </p>
        </div>
      )}

      <div className={`space-y-3 ${isMobile ? 'px-4 py-4' : ''}`}>
        {visibleIntegrations.map(integration =>
          integration.provider === 'linkedin' ? (
            linkedinCard && (
              <IntegrationCard
                key="linkedin"
                integration={linkedinCard}
                isConnecting={linkedinConnecting}
                isDisconnecting={linkedinDisconnecting}
                onConnect={runConnect}
                onDisconnect={handleDisconnect}
                onMarkFixed={handleMarkRestrictionFixed}
              />
            )
          ) : (
            <IntegrationCard
              key={integration.provider}
              integration={integration}
              isConnecting={connectingProvider === integration.provider}
              isDisconnecting={disconnectingProvider === integration.provider}
              onConnect={runConnect}
              onDisconnect={handleDisconnect}
              onMarkFixed={handleMarkRestrictionFixed}
            />
          )
        )}
      </div>

      {chooser}

      <GatewayConnectDialogs connect={gatewayConnect} />
    </div>
  )
}

function IntegrationCard({
  integration,
  isConnecting,
  isDisconnecting,
  onConnect,
  onDisconnect,
  onMarkFixed,
}: {
  integration: IntegrationResponseDto
  isConnecting: boolean
  isDisconnecting: boolean
  onConnect: (provider: string) => void
  onDisconnect: (provider: string) => void
  onMarkFixed: (provider: string) => Promise<void>
}) {
  const navigate = useNavigate()
  const isLoading = isConnecting || isDisconnecting

  // Expandable "Restricted" details. Local per-card; collapses on its own when
  // the restriction clears (isRestricted flips false on the Pusher refetch).
  const [restrictionExpanded, setRestrictionExpanded] = useState(false)
  const [markingFixed, setMarkingFixed] = useState(false)

  // The provider restricted this account from publishing (e.g. Meta code 25).
  // The token is healthy, so needsReconnect wins when both are set — a dead
  // token blocks everything, including the restriction probe.
  const showRestricted =
    integration.isConnected && integration.isRestricted && !integration.needsReconnect

  // Facebook authorized but no Page bound yet (the user manages zero or several
  // Pages, or an older connect left the integration page-less). The token is
  // still valid, so route them straight to the picker to finish — no re-OAuth.
  const facebookNeedsPage =
    integration.provider === 'facebook' &&
    integration.isConnected &&
    !integration.needsReconnect &&
    !integration.config?.pageId

  // LinkedIn authorized via the Company-Page flow but no Page chosen yet
  // (config.linkedinTarget === null). Same shape as facebookNeedsPage: the token
  // is valid, so route them to the picker to finish — never show / publish as the
  // personal profile while a Company-Page pick is pending.
  const needsLinkedInTarget = linkedinNeedsTarget(integration)

  return (
    <div className="flex items-center gap-4 rounded-xl border border-line bg-bg-2 p-4 transition-all hover:border-line-bright hover:shadow-[var(--shadow-card)]">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-bg-3">
        {integration.logoUrl ? (
          <img
            src={integration.logoUrl}
            alt={integration.displayName}
            className="h-6 w-6 object-contain"
          />
        ) : (
          <Plug size={20} className="text-paper-faint" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-paper">{integration.displayName}</span>
          {integration.isConnected &&
            !integration.needsReconnect &&
            !integration.isRestricted &&
            !facebookNeedsPage &&
            !needsLinkedInTarget && (
              <span className="text-success inline-flex items-center rounded-full bg-[color:var(--success-tint)] px-2 py-0.5 text-xs font-medium">
                Connected
              </span>
            )}
          {(facebookNeedsPage || needsLinkedInTarget) && (
            <span className="inline-flex items-center rounded-full bg-[color:var(--alert-warning-bg)] px-2 py-0.5 text-xs font-medium text-[color:var(--alert-warning-text)]">
              Action needed
            </span>
          )}
          {integration.needsReconnect && (
            <span className="inline-flex items-center rounded-full bg-[color:var(--alert-warning-bg)] px-2 py-0.5 text-xs font-medium text-[color:var(--alert-warning-text)]">
              Reconnect required
            </span>
          )}
          {showRestricted && (
            <button
              type="button"
              onClick={() => setRestrictionExpanded(prev => !prev)}
              className="inline-flex items-center gap-1 rounded-full border border-[color:var(--alert-error-text)] bg-[color:var(--alert-error-bg)] px-2 py-0.5 text-xs font-medium text-[color:var(--alert-error-text)]"
            >
              Restricted
              <ChevronDown
                size={12}
                className={cn('transition-transform', restrictionExpanded && 'rotate-180')}
              />
            </button>
          )}
        </div>
        {needsLinkedInTarget ? (
          <p className="text-sm text-[color:var(--alert-warning-text)]">
            No Company Page selected. Choose one to publish.
          </p>
        ) : integration.isConnected && integration.provider === 'linkedin' ? (
          <LinkedInTargetSummaryRow integration={integration} />
        ) : (
          <>
            {integration.isConnected && integration.config?.handle && (
              <p className="text-sm text-paper-dim">@{integration.config.handle}</p>
            )}
            {integration.isConnected && integration.config?.pageName && (
              <p className="text-sm text-paper-dim">{integration.config.pageName}</p>
            )}
            {facebookNeedsPage && (
              <p className="text-sm text-[color:var(--alert-warning-text)]">
                No Page selected. Pick one to publish.
              </p>
            )}
            {integration.isConnected && integration.config?.defaultBoardName && (
              <p className="text-sm text-paper-dim">{integration.config.defaultBoardName}</p>
            )}
          </>
        )}
        <AnimatePresence initial={false}>
          {showRestricted && restrictionExpanded && (
            <motion.div
              key="restriction-details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-3 rounded-[10px] border border-[color:var(--alert-error-text)] bg-[color:var(--alert-error-bg)] p-3 text-sm">
                <p className="font-medium text-[color:var(--alert-error-text)]">
                  {integration.displayName} blocked posting to this account.
                </p>
                <p className="mt-1 text-paper-dim">
                  {integration.restrictionReason ||
                    `We paused ${integration.displayName} for now. Your other platforms keep posting. Sort it out with ${integration.displayName}, then hit Reset here to resume.`}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={markingFixed}
                    onClick={async () => {
                      setMarkingFixed(true)
                      try {
                        await onMarkFixed(integration.provider)
                      } finally {
                        setMarkingFixed(false)
                      }
                    }}
                    className="text-ink-on-acid inline-flex items-center gap-1.5 rounded-full bg-acid px-3 py-1 text-[13px] font-medium hover:opacity-90 disabled:opacity-60"
                  >
                    {markingFixed ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <RotateCcw size={13} />
                    )}
                    Reset
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {!integration.isAvailable && <p className="text-xs text-paper-faint">Coming soon</p>}
      </div>

      <div className="flex-shrink-0">
        {!integration.isAvailable ? (
          <div />
        ) : facebookNeedsPage ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/integrations/facebook/select')}
              disabled={isLoading}
              className="text-ink-on-acid inline-flex items-center gap-1.5 rounded-[8px] bg-acid px-3 py-1.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
            >
              <Plug size={14} />
              Select a Page
            </button>
            <button
              onClick={() => onDisconnect(integration.provider)}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 rounded-[8px] border border-line-bright px-3 py-1.5 text-[13px] text-paper-dim transition-colors hover:border-[color:var(--alert-error-border)] hover:bg-[color:var(--alert-error-bg)] hover:text-[color:var(--alert-error-text)] disabled:opacity-50"
            >
              {isDisconnecting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Unplug size={14} />
              )}
              Disconnect
            </button>
          </div>
        ) : needsLinkedInTarget ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/integrations/linkedin/select')}
              disabled={isLoading}
              className="text-ink-on-acid inline-flex items-center gap-1.5 rounded-[8px] bg-acid px-3 py-1.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
            >
              <Plug size={14} />
              Choose a Page
            </button>
            <button
              onClick={() => onDisconnect(integration.provider)}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 rounded-[8px] border border-line-bright px-3 py-1.5 text-[13px] text-paper-dim transition-colors hover:border-[color:var(--alert-error-border)] hover:bg-[color:var(--alert-error-bg)] hover:text-[color:var(--alert-error-text)] disabled:opacity-50"
            >
              {isDisconnecting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Unplug size={14} />
              )}
              Disconnect
            </button>
          </div>
        ) : integration.isConnected ? (
          <button
            onClick={() => onDisconnect(integration.provider)}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 rounded-[8px] border border-line-bright px-3 py-1.5 text-[13px] text-paper-dim transition-colors hover:border-[color:var(--alert-error-border)] hover:bg-[color:var(--alert-error-bg)] hover:text-[color:var(--alert-error-text)] disabled:opacity-50"
          >
            {isDisconnecting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Unplug size={14} />
            )}
            Disconnect
          </button>
        ) : (
          <button
            onClick={() => onConnect(integration.provider)}
            disabled={isLoading}
            className="text-ink-on-acid inline-flex items-center gap-1.5 rounded-[8px] bg-acid px-3 py-1.5 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px disabled:opacity-50"
          >
            {isConnecting ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />}
            Connect
          </button>
        )}
      </div>
    </div>
  )
}

function LinkedInTargetSummaryRow({ integration }: { integration: IntegrationResponseDto }) {
  const navigate = useNavigate()
  const { type, name: targetName, avatarUrl } = resolveLinkedInTargetSummary(integration)

  // The connected LinkedIn destination — Personal profile or a Company Page.
  // Users can switch to any other admin'd Page (or back to Personal) via the
  // picker.
  const FallbackIcon = type === 'person' ? User : Building2
  return (
    <div className="mt-1 flex items-center gap-2">
      <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt={targetName} className="h-5 w-5 object-cover" />
        ) : (
          <FallbackIcon size={12} className="text-paper-faint" />
        )}
      </div>
      <span className="min-w-0 truncate text-sm text-paper">{targetName}</span>
      <button
        type="button"
        onClick={() => navigate('/integrations/linkedin/select')}
        className="text-xs font-medium text-paper-dim underline-offset-2 transition-colors hover:text-paper hover:underline"
      >
        Change
      </button>
    </div>
  )
}
